import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import Navbar from './components/layout/Navbar';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import Projects from './pages/Projects';
import Rooms from './pages/Rooms';
import RoomForm from './components/rooms/RoomForm';
import Appliances from './pages/Appliances';
import ApplianceDetail from './pages/ApplianceDetail';
import ApplianceForm from './components/appliances/ApplianceForm';
import Contacts from './pages/Contacts';
import ContactForm from './components/contacts/ContactForm';
import Expenses from './pages/Expenses';
import HistoryPage from './pages/History';
import Search from './pages/Search';
import CalendarView from './pages/CalendarView';
import ProjectIntake from './pages/ProjectIntake';
import ProjectDetail from './pages/ProjectDetail';
import HomeProfile from './pages/HomeProfile';
import Consumables from './pages/Consumables';
import ShoppingList from './pages/ShoppingList';
import ProjectRescope from './pages/ProjectRescope';
import ProjectChat from './pages/ProjectChat';
import TaskSuggestions from './pages/TaskSuggestions';
import Homes from './pages/Homes';
import Admin from './pages/Admin';
import Onboarding from './pages/Onboarding';
import JoinHome from './pages/JoinHome';
import Login from './pages/Login';
import Landing from './pages/Landing';
import FeatureRequests from './pages/FeatureRequests';
import Tools from './pages/Tools';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [homes, setHomes] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);

  const loadAuth = () => {
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then(({ user, homes }) => {
        setUser(user || null);
        setHomes(homes || []);
        setAuthLoading(false);
      })
      .catch(() => setAuthLoading(false));
  };

  useEffect(() => { loadAuth(); }, []);

  if (authLoading) return <div className="loading">Loading…</div>;

  return (
    <ToastProvider>
    <ConfirmProvider>
    <BrowserRouter>
      {user ? (
        homes.length === 0 ? (
          // Authenticated but no home yet — show onboarding
          <Routes>
            <Route path="/join/:token" element={<JoinHome onJoined={loadAuth} />} />
            <Route path="*" element={<Onboarding onCreated={loadAuth} />} />
          </Routes>
        ) : (
          <AppProvider>
            <Navbar user={user} homes={homes} onLogout={() => setUser(null)} onHomeSwitch={loadAuth} />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/rooms/new" element={<RoomForm />} />
                <Route path="/rooms/:id/edit" element={<RoomForm />} />
                <Route path="/appliances" element={<Appliances />} />
                <Route path="/appliances/new" element={<ApplianceForm />} />
                <Route path="/appliances/:id" element={<ApplianceDetail />} />
                <Route path="/appliances/:id/edit" element={<ApplianceForm />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/new" element={<ContactForm />} />
                <Route path="/contacts/:id/edit" element={<ContactForm />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/search" element={<Search />} />
                <Route path="/calendar" element={<CalendarView />} />
                <Route path="/projects/intake" element={<ProjectIntake />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/projects/:id/rescope" element={<ProjectRescope />} />
                <Route path="/projects/:id/chat" element={<ProjectChat />} />
                <Route path="/home-profile" element={<HomeProfile />} />
                <Route path="/consumables" element={<Consumables />} />
                <Route path="/shopping" element={<ShoppingList />} />
                <Route path="/tasks/suggestions" element={<TaskSuggestions />} />
                <Route path="/homes" element={<Homes homes={homes} user={user} onChanged={loadAuth} />} />
                <Route path="/tools" element={<Tools />} />
                <Route path="/feature-requests" element={<FeatureRequests />} />
                <Route path="/admin" element={user.isAdmin ? <Admin currentUserId={user._id} /> : <Navigate to="/" replace />} />
                <Route path="/join/:token" element={<JoinHome onJoined={loadAuth} />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </AppProvider>
        )
      ) : (
        <Routes>
          <Route path="/join/:token" element={<JoinHome onJoined={loadAuth} />} />
          <Route path="/login" element={<Landing />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      )}
    </BrowserRouter>
    </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
